import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject, filter, pairwise, startWith, takeUntil } from 'rxjs';
import { selectNraIndicator, selectRegistrationType, selectStateCode } from '../../../account-info-header/reducers';
import { OptionDropdown, SelectControl } from '../../../create-request/models/OptionDropdownSelect';
import { DistributionReason } from '../../../create-request/models/internal-transfer/DistributionReason';
import { ValidateInputService } from '../../../shared/services/validate-input.service';
import { Constants, RothWarnings, amountTypeDict, distributionReasons, fedTaxErrorMap, pdfLabelsSubmitDetails, registrationTypes, stateTaxErrorMap, transactionDetailLabels } from '../../../shared/utilities/Constants';
import * as CustomValidators from '../../../shared/utilities/FormValidators';
import { percentFormatter } from '../../../shared/utilities/PercentFormatter';
import { percentAllowedChars } from '../../../shared/utilities/regex-patterns';

import { selectAchCanDisplayStateTax, selectAchDefaultFedTaxPercent, selectAchDefaultStateTaxPercent, selectAchDistributionReason, selectAchDistributionReasonsList, selectAchStateCode, selectAchStateTypeIndicator, selectAchVerifyCallWarning, selectStandingAuthFormPrefilledState } from '../../reducers';
import { TaxWithholdingFields } from '../../models/standingAuthorizationForm';
import { WarningOrError } from '../../../create-request/models/shared/WarningOrError';
import { ActionCenterRedirectService } from '../../../shared/services/ActionCenterRedirect.service';
import { Hyperlink } from '../../../shared/types/Hyperlink';
import { FeatureFlagService } from '../../../shared/services/feature-flag-service';
import { StandingAuthFormActions } from '../../actions';

@Component({
  standalone: false,
  selector: 'mm-sa-ach-tax-withholdings',
  templateUrl: './sa-ach-tax-withholdings.component.html',
  styleUrls: ['./sa-ach-tax-withholdings.component.scss']
})
export class SaAchTaxWithholdingsComponent implements OnInit {
  @Input() parentForm: UntypedFormGroup;
  @Output() showCashieringIRAAgreementModal = new EventEmitter<boolean>();
  @Input() taxWithholdings: TaxWithholdingFields;
  @Input() taxWithholdingsFormGroupDetails: TaxWithholdingFields | undefined;
  @Input() isRecurring: boolean;
  distributioncontroller: any;
  taxWithholdingText: string = Constants.taxWithholdingText;
  nraText: string = Constants.nraText;
  nraIndicator: boolean;
  readonly fedTaxControl: string = 'fedTaxPercent';
  readonly stateTaxControl: string = 'stateTaxPercent';
  //readonly distributionReasonController: string = 'distributionReason';
  readonly formControlName: string = 'distributionReason';
  percentAllowedChars = percentAllowedChars;
  defaultFedTax: string;
  defaultStateTax: string;
  destroy$: Subject<boolean> = new Subject<boolean>();
  stateTypeIndicator: string;
  stateCode: string;
  achCTWEAuthorizationError: string = Constants.achCTWEAuthorizationError;
  rmdAlertText: string = Constants.rmdAlertText;
  regType: string;
  shouldShowStateTax: boolean;
  selectedDistributionReason: string;
  distributionReasonsList: DistributionReason[];
  distributionReasonModel: SelectControl = {
    title: 'Distribution reason',
    controlName: 'distributionReason',
    options: [],
    validationMessage: 'Distribution reason needs to be selected',
  };
  noInstructionsErrorLink: Hyperlink = {
    url: '',
    text: 'create a new profile',
    target: '_blank',
    isActionCenterHyperLink: true
  }
  distributionReasonTooltip: string = Constants.itCheckDistributionReasonTooltip;
  qualifiedDistReason: string = pdfLabelsSubmitDetails.qualifiedDistReason;
  qualifiedDistReasonWarning: string = RothWarnings.QualifiedDistributionWarning;
  is100TweEligible: boolean;
  warningMessageList: WarningOrError[];
  isEditSubmittedProfile: boolean;
  showTweCalculatorModal = new EventEmitter<boolean>();
  isFedTax$ = new BehaviorSubject<boolean>(false);
  disableStateTax: boolean;
  saStateTaxLabel: string = transactionDetailLabels.stateTax;

  constructor(
    private store: Store,
    public validationService: ValidateInputService,
    private fb: UntypedFormBuilder,
    private actionCenterRedirectService: ActionCenterRedirectService,
    private featureFlagService: FeatureFlagService
  ) { }

  ngOnInit(): void {
    const taxWithholdingsFormGroup = this.fb.group({
      distributionReason: this.constructDistReasonFormControl(),
      fedTaxPercent: this.GetFedTaxFormControl(),
      stateTaxPercent: this.GetStateTaxFormControl(),
    });
    this.parentForm.setControl('taxWithholdingsFormGroup', taxWithholdingsFormGroup);

    this.store.select(selectAchDefaultFedTaxPercent).pipe(takeUntil(this.destroy$)).subscribe(defaultFedTax => this.defaultFedTax = defaultFedTax);
    this.store.select(selectAchDefaultStateTaxPercent).pipe(takeUntil(this.destroy$)).subscribe(defaultStateTax => this.defaultStateTax = defaultStateTax);
    this.store.select(selectAchDistributionReason)
    .pipe(
      takeUntil(this.destroy$),
      startWith(''),
      pairwise(),
      filter(([previousDistributionReason, selectedDistributionReason]) => selectedDistributionReason !== previousDistributionReason)
    )
    .subscribe(([previousDistributionReason, selectedDistributionReason]) => {
      this.selectedDistributionReason = selectedDistributionReason;
      if (selectedDistributionReason === this.qualifiedDistReason) {
        this.resetAndDisableTaxFields();
      }
      if (previousDistributionReason === this.qualifiedDistReason && !this.nraIndicator) {
        this.enableTaxFields();
      }
    });

    this.store.select(selectAchVerifyCallWarning).pipe(takeUntil(this.destroy$)).subscribe(warningMessageList => this.warningMessageList = warningMessageList);
    this.store.select(selectNraIndicator).pipe(takeUntil(this.destroy$)).subscribe(_nraIndicator => this.nraIndicator = _nraIndicator ?? false);
    this.store.select(selectAchStateTypeIndicator).pipe(takeUntil(this.destroy$)).subscribe(_stateTypeIndicator => this.stateTypeIndicator = _stateTypeIndicator);
    this.store.select(selectAchStateCode).pipe(takeUntil(this.destroy$)).subscribe(_stateCode => this.stateCode = _stateCode);
    this.store.select(selectRegistrationType).pipe(takeUntil(this.destroy$)).subscribe(_regType => this.regType = _regType);
    this.store.select(selectAchCanDisplayStateTax).pipe(takeUntil(this.destroy$)).subscribe(_shouldShowStateTax => this.shouldShowStateTax = _shouldShowStateTax);
    this.store.select(selectAchDistributionReasonsList).pipe(takeUntil(this.destroy$)).subscribe(_distributionReasonsList => this.distributionReasonsList = _distributionReasonsList);
    this.store.select(selectStandingAuthFormPrefilledState).pipe(takeUntil(this.destroy$)).subscribe(prefilledFlowState => this.isEditSubmittedProfile = prefilledFlowState.state === 'started');
    this.store.select(selectStateCode).pipe(takeUntil(this.destroy$)).subscribe(stateCode => {
      if (stateCode && !this.nraIndicator) {
        this.saStateTaxLabel = `${transactionDetailLabels.stateTax} (${stateCode})`;
      }
    });

    this.distributionReason.valueChanges
          .pipe(takeUntil(this.destroy$))
          .subscribe((val) => {
            if (this.taxWithholdings.distributionReasonMap) {
              let distributionReason =
                this.taxWithholdings.distributionReasonMap.filter(
                  (element) => element.description === val
                )[0] ?? ({} as DistributionReason);
              this.store.dispatch(
                StandingAuthFormActions.distributionReasonChanged({
                  distributionReason: distributionReason,
                  transactionType: 'ach',
                })
              );
            }
          });

    if (this.isEducationalSavings()) {
      this.fedTaxPercent.disable();
      this.stateTaxPercent.disable();
    }
    this.disableStateTaxFormControl();

    this.distributionReasonModel.options = this.distributionReasonsList.filter((elem) => elem.description !== 'Select One').map((element) => new OptionDropdown(element.description, element.description));
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  get taxWithholdingsFormGroup() {
    return this.parentForm.controls.taxWithholdingsFormGroup as UntypedFormGroup;
  }

  get distributionReasonControl(): FormControl{
    return (this.taxWithholdingsFormGroup?.get(this.formControlName)) as FormControl;
  }

  get showRmdBanner() {
    return this.distributionReasonControl.value === distributionReasons.rmd;
  }

  get fedTaxPercent() {
    return this.taxWithholdingsFormGroup?.controls.fedTaxPercent;
  }

  get stateTaxPercent() {
    return this.taxWithholdingsFormGroup?.controls.stateTaxPercent;
  }

  get isFedTaxInvalid(): boolean {
    return ((this.fedTaxPercent?.touched && this.fedTaxPercent?.invalid) || this.fedAndStateTaxSumError !== '');
  }

  get isStateTaxInvalid(): boolean {
    return ((this.stateTaxPercent?.touched && this.stateTaxPercent?.invalid) || this.fedAndStateTaxSumError !== '');
  }

  onFedTaxBlur(): void {
    if (this.fedTaxPercent.valid) {
      this.fedTaxPercent?.setValue(percentFormatter(this.fedTaxPercent.value));
    }
  }

  onStateTaxBlur(): void {
    if (this.stateTaxPercent.valid) {
      this.stateTaxPercent?.setValue(percentFormatter(this.stateTaxPercent.value));
    }
  }

  get fedTaxError() {
    if (this.fedTaxPercent && this.fedTaxPercent.errors) {
      const errorKeys: string[] = Object.keys(this.fedTaxPercent.errors ?? {});
      if (errorKeys.length > 0) {
        return fedTaxErrorMap[errorKeys[0] as keyof typeof fedTaxErrorMap];
      }
    }
  }

  get stateTaxError() {
    if (this.stateTaxPercent && this.stateTaxPercent.errors) {
      const errorKeys: string[] = Object.keys(this.stateTaxPercent.errors ?? {});
      if (errorKeys.length > 0) {
        return stateTaxErrorMap[errorKeys[0] as keyof typeof stateTaxErrorMap];
      }
    }
  }

  get fedAndStateTaxSumError(): string {
    if (this.parentForm && this.parentForm.errors) {
      const errorKeys: string[] = Object.keys(
        this.parentForm.errors ?? {}
      );
      if (errorKeys.length > 0) {
        return 'Total tax withholding cannot exceed 99.99%. Update values or request a one-time check to withhold 100%';
      }
    }
    return '';
  }

  disableStateTaxFormControl() {
    if (!(this.shouldShowStateTax || this.isEducationalSavings()) || this.nraIndicator){
      this.stateTaxPercent?.disable();
      this.disableStateTax = true;
    }
  }

  get distributionReasonFormatted() {
    return this.taxWithholdings?.distributionReason ? this.taxWithholdings.distributionReason : '';
  }

  get distributionReason() {
    return this.taxWithholdingsFormGroup?.controls?.distributionReason;
  }

  get fedTaxFormatted() {
    return percentFormatter(this.taxWithholdings?.fedTaxPercent);
  }

  get stateTaxFormatted() {
    return percentFormatter(this.taxWithholdings?.stateTaxPercent);
  }

  //If an advisor's current account level federal election is 10% or higher and they're electing something 9% (e.g. 15% -> 5% or 30% -> 9%) or under
  //OR if an advisors current account level federal election is 9% or lower and they're electing a lower percentage (e.g. 9% -> 5% or 2% -> 1%)
  //We DO NOT ALLOW them to proceed when the CTWE Warning Message appears
  get CTWEWarningMessage() {
    if(this.warningMessageList){
      for (let i = 0; i < this.warningMessageList.length; i++) {
        if ((this.warningMessageList[i].messageField === 'CTWE' && this.isEditSubmittedProfile)) {
          return true;
        } else {
          return false;
        }
      }
    }
  }

  isEducationalSavings(): boolean {
    return this.regType === registrationTypes.educationalSavings;
  }

  displayIraAgreementModal() {
    this.showCashieringIRAAgreementModal.emit(true);
  }

  resetAndDisableTaxFields(): void {
    this.fedTaxPercent?.setValue(Constants.zeroPercent);
    this.fedTaxPercent?.disable();
    this.stateTaxPercent?.setValue(Constants.zeroPercent);
    this.stateTaxPercent?.disable();
  }

  enableTaxFields(): void {
    this.fedTaxPercent?.setValue(percentFormatter(this.defaultFedTax));
    this.fedTaxPercent?.enable();
    this.stateTaxPercent?.setValue(percentFormatter(this.defaultStateTax));
    if (!this.disableStateTax){
      this.stateTaxPercent?.enable();
    }
  }

  constructDistReasonFormControl() {
    return new UntypedFormControl(this.distributionReasonFormatted, [Validators.required]);
  }

  GetFedTaxFormControl() {
    return new UntypedFormControl(this.fedTaxFormatted, [
      Validators.required,
      CustomValidators.zeroOrGreater,
      CustomValidators.wholeNumber,
      this.is100TweEligible
        ? CustomValidators.oneTimeCheckGreaterThan100
        : CustomValidators.greaterThan99FedRequestCheck,
      Validators.maxLength(10),
    ]);
  }

  GetStateTaxFormControl() {
    return new UntypedFormControl(this.stateTaxFormatted, [
      Validators.required,
      CustomValidators.zeroOrGreater,
      this.is100TweEligible
        ? CustomValidators.oneTimeCheckGreaterThan99State
        : CustomValidators.greaterThan99State,
      Validators.maxLength(10),
    ]);
  }

  actionCenterRedirect() {
    this.actionCenterRedirectService.redirectToActionCenter();
  }

  onStateTaxCalculatorClick() {
    this.showTweCalculatorModal.emit(true);
    this.isFedTax$.next(false);
  }

  onFedTaxCalculatorClick() {
    this.showTweCalculatorModal.emit(true);
    this.isFedTax$.next(true);
  }

  setTaxPercentValue(fedTaxPercentage: string, stateTaxPercentage: string) {
    if (fedTaxPercentage !== '' && fedTaxPercentage !== null) {
      let fed = isNaN(Number(fedTaxPercentage)) ? '0%' : fedTaxPercentage + '%';
      this.taxWithholdingsFormGroup.controls.fedTaxPercent.setValue(fed);
    }
    else if (stateTaxPercentage !== '' && stateTaxPercentage !== null) {
      let state = isNaN(Number(stateTaxPercentage)) ? '0%' : stateTaxPercentage + '%';
      this.taxWithholdingsFormGroup.controls.stateTaxPercent.setValue(state);
    }
  }

  get amount() {
    return this.parentForm.controls.amount;
  }
}
